/**
 * The "Active Change Request" card of {@link ChangeRequestsTab}.
 *
 * Split out of ChangeRequestsTab (509 lines) for Customer review 11 Sep (review
 * msg 28). The markup is unchanged; the three per-field rows (date / price /
 * quantity), which were three copies of one block, render through
 * {@link ProposedFieldRow}. The tab only mounts this card when
 * `hasOpenChangeRequest` says a change is genuinely pending, so the per-field
 * accept / reject state lives here with it.
 */
import type { ReactNode } from 'react';
import { useFormatters } from '../../contexts/PreferencesContext';
import { CheckCircle, XCircle, Clock, Calendar, Package, DollarSign, User, Loader2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { PurchaseOrderLine } from '../../lib/api';
import { getOtifDateVariance } from '../../utils/otifDateVariance';
import { getStatusBadge, type ChangeField } from './changeRequestsTabHelpers';
import { useChangeRequestActions } from './useChangeRequestActions';
import { canActOnProposal, isPhantomChangeRequest, isNoOpDateProposal } from './pendingProposal';
import { CUSTOMER_REQUIRED_DATE_SHORT, SUPPLIER_PROMISE_DATE_SHORT } from '../../config/dateLabels';

interface ActiveChangeRequestCardProps {
  line: PurchaseOrderLine;
  /** The viewer raised this proposal (Cancel instead of Accept / Reject). */
  isProposer: boolean;
  /** The viewer may decide this proposal. */
  isReviewer: boolean;
  formatDate: (value: string) => string;
  formatDateTime: (value: string) => string;
  onUpdate?: () => void;
}

interface ProposedFieldRowProps {
  field: ChangeField;
  icon: LucideIcon;
  label: string;
  from: ReactNode;
  to: ReactNode;
  showActions: boolean;
  isProposer: boolean;
  loading: string | null;
  onAccept: () => void;
  onReject: () => void;
  onCancel: () => void;
  rejectForm: ReactNode;
  /** CR-7739: why this proposal cannot be accepted here (Accept is not offered). */
  acceptBlocked?: string;
}

/** Placeholder for the optional counter input, in the units of the field. */
function counterPlaceholder(field: ChangeField): string {
  if (field === 'date') return 'Your date instead (optional)';
  if (field === 'price') return 'Your unit price instead (optional)';
  return 'Your quantity instead (optional)';
}

/** What happens if the counter is left empty, said in the user's terms. */
function counterHint(field: ChangeField): string {
  const what = field === 'date' ? 'date' : field === 'price' ? 'unit price' : 'quantity';
  return `leave it empty to reject without offering a ${what}`;
}

/** One proposed field — from → to, plus Cancel (proposer) or Accept / Reject (reviewer). */
function ProposedFieldRow({
  field, icon: Icon, label, from, to, showActions, isProposer, loading, onAccept, onReject, onCancel, rejectForm,
  acceptBlocked,
}: ProposedFieldRowProps) {
  return (<>
    <div className="flex items-center gap-3 bg-white rounded-lg p-3 border">
      <Icon className="h-4 w-4 text-orange-600 flex-shrink-0" />
      <div className="flex-1">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-sm">
          <span className="text-muted-foreground">{from}</span>
          <span className="mx-2 text-muted-foreground">&rarr;</span>
          {to}
        </div>
      </div>
      {showActions && (
        <div className="flex items-center gap-1">
          {isProposer ? (
            <button onClick={onCancel} disabled={!!loading}
              className="flex items-center gap-1 px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-xs font-medium disabled:opacity-50">
              {loading === field ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
              Cancel
            </button>
          ) : (<>
            {acceptBlocked ? (
              <span className="text-xs text-muted-foreground mr-1" data-testid="accept-blocked">{acceptBlocked}</span>
            ) : (
            <button onClick={onAccept} disabled={!!loading}
              className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-medium disabled:opacity-50">
              {loading === field ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
              Accept
            </button>
            )}
            <button onClick={onReject} disabled={!!loading}
              className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium disabled:opacity-50">
              <XCircle className="h-3 w-3" /> Reject
            </button>
          </>)}
        </div>
      )}
    </div>
    {rejectForm}
  </>);
}

export default function ActiveChangeRequestCard({
  line, isProposer, isReviewer, formatDate, formatDateTime, onUpdate,
}: ActiveChangeRequestCardProps) {
  // CR-8276: the line's quantities arrive as numeric(12,3) text; show them as the form does.
  const { formatQuantity } = useFormatters();
  // CR-4688 rework: the date row's "active" flag must come ONLY from
  // supplier_proposed_date. Once the backend accepts a date change, it
  // nulls supplier_proposed_date and keeps supplier_promise_date at the
  // accepted value — falling back to supplier_promise_date here made the
  // row stay "Open" even though the audit trail showed it as Accepted.
  const proposedDateTo = line.supplier_proposed_date || '';
  // R-378: read the SERVER's grain-aware ODD first (purchasing.line_odd).
  // Re-deriving it from the stored columns gets schedule-grain customers
  // wrong: at that grain the anchor is the slot date.
  const proposedDateFrom = line.odd_date || line.original_required_date
    || line.customer_required_date || '';
  const hasProposedDate = !!(proposedDateTo && proposedDateFrom && proposedDateTo !== proposedDateFrom);
  // CR-5479: classify the proposed date against the line's ODD (odd_date,
  // the date the verdict is measured against) + OTIF window so the row
  // carries the same amber/red + variance indicator as the PO Line Items
  // table and the grouped cards.
  const proposedDateVariance = getOtifDateVariance(
    proposedDateTo, line.odd_date ?? line.customer_required_date, line.supplier_otif_window,
  );
  const hasProposedPrice = !!line.supplier_proposed_unit_price;
  const hasProposedQty = !!line.supplier_proposed_qty;
  const activeChangeFields = [hasProposedDate && 'date' as const, hasProposedPrice && 'price' as const, hasProposedQty && 'quantity' as const].filter(Boolean) as ChangeField[];
  const proposalCount = activeChangeFields.length;
  const quantityAcceptBlocked = hasProposedQty && !!line.quantity_proposals_hidden;
  // CR-7166: role-appropriate label for date changes. CR-7488: the action
  // toasts name the date with this same label.
  const dateLabel = line.acknowledge_status === 'change_proposed_customer' ? CUSTOMER_REQUIRED_DATE_SHORT : SUPPLIER_PROMISE_DATE_SHORT;

  const {
    fieldActionLoading, rejectField, setRejectField, rejectReason, setRejectReason,
    rejectCounter, setRejectCounter,
    handleAcceptField, handleRejectField, handleAcceptAll, handleCancelField,
  } = useChangeRequestActions(line, activeChangeFields, dateLabel, onUpdate);

  // CR-4688 round 3 rework: per-field actions whenever a specific field is in
  // play — the bulk modals are date-only and hit the date endpoint.
  // CR-7309: and only when the change is actionable — the gate the bulk pair
  // already had (CR-7277). The card decides this itself, whatever mounts it:
  // on ERP-imported lines supplier_proposed_date is the legacy Review Date
  // (CR-7294), and a date row drawn from it armed a per-field Accept that
  // rewrote the promise date on a line nobody had proposed anything for.
  const actionable = canActOnProposal(line);
  const showPerFieldActions = (isReviewer || isProposer) && actionable;
  const phantomChange = isPhantomChangeRequest(line);
  const noOpDateChange = isNoOpDateProposal(line);
  // Customer review 11 Sep (item 28): this card only mounts for a
  // change_proposed_* status, so the proposer is always known — the old
  // 'Unknown' fallback belonged to lines that never had a proposal.
  const proposedBy = line.acknowledge_status === 'change_proposed_customer' ? 'Customer' : 'Supplier';

  // CR-4862: the reject reason form renders inline under the field being
  // rejected, or at the bottom for "Reject All".
  const renderRejectForm = (forField: ChangeField | 'all') => (
    <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
      <p className="text-sm font-medium text-red-800">
        {forField === 'all' ? 'Reject all changes' : `Reject ${forField === 'date' ? dateLabel : forField === 'price' ? 'Unit Price' : 'Quantity'} change`}
      </p>
      <input
        type="text"
        placeholder="Reason for rejection (required)"
        value={rejectReason}
        onChange={(e) => setRejectReason(e.target.value)}
        className="w-full px-3 py-2 border rounded text-sm"
        autoFocus
      />
      {/* EXPEDITING-PARITY PR 2 (G8) — the counter. Optional, and only on a
          single field: a value answers ONE proposal, so "reject all" has
          nothing to attach it to. Left empty the rejection closes the request,
          which is what it has always done; filled in, the same click sends
          your own value back and the line waits on them instead of stopping at
          Change Declined. */}
      {forField !== 'all' && (
        <div className="space-y-1">
          <input
            type={forField === 'date' ? 'date' : 'number'}
            step={forField === 'price' ? '0.01' : '1'}
            min={forField === 'date' ? undefined : '0'}
            placeholder={counterPlaceholder(forField)}
            value={rejectCounter}
            onChange={(e) => setRejectCounter(e.target.value)}
            className="w-full px-3 py-2 border rounded text-sm"
          />
          <p className="text-xs text-red-700">
            {rejectCounter
              ? 'They will be asked to accept or reject your value.'
              : `Optional — ${counterHint(forField)}`}
          </p>
        </div>
      )}
      <div className="flex gap-2">
        <button
          onClick={handleRejectField}
          disabled={!rejectReason.trim() || !!fieldActionLoading}
          className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 disabled:opacity-50"
        >
          {fieldActionLoading === forField ? <Loader2 className="h-3 w-3 animate-spin inline mr-1" /> : null}
          {forField !== 'all' && rejectCounter ? 'Reject and Counter' : 'Confirm Reject'}
        </button>
        <button
          onClick={() => { setRejectField(null); setRejectReason(''); setRejectCounter(''); }}
          className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const rowProps = (field: ChangeField) => ({
    field,
    showActions: showPerFieldActions,
    isProposer,
    loading: fieldActionLoading,
    onAccept: () => handleAcceptField(field),
    onReject: () => setRejectField(field),
    onCancel: () => handleCancelField(field),
    rejectForm: rejectField === field ? renderRejectForm(field) : null,
    // CR-7739: with quantity changes switched off for the line's site the
    // server refuses the accept (nothing is applied or written back); the
    // proposal can still be rejected, which clears it.
    acceptBlocked: field === 'quantity' && quantityAcceptBlocked
      ? 'Quantity changes are switched off for this site' : undefined,
  });

  return (
    <div className="border-2 border-orange-300 rounded-lg bg-orange-50/50 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 bg-orange-100/60 border-b border-orange-200">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-orange-600" />
          <span className="font-semibold text-orange-800">Active Change Request</span>
        </div>
        {getStatusBadge('open')}
      </div>
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <User className="h-3.5 w-3.5" />
          <span>
            {proposedBy}
            {line.acknowledged_by_details?.full_name && ` (${line.acknowledged_by_details.full_name})`}
            {line.acknowledge_date && ` on ${formatDateTime(line.acknowledge_date)}`}
          </span>
        </div>

        <div className="space-y-2">
          {hasProposedDate && (
            <ProposedFieldRow
              {...rowProps('date')}
              icon={Calendar}
              label={dateLabel}
              // CR-8596: this is the ODD the proposal is judged against (R-378),
              // not the date the request card records as "Old" (the value at
              // proposal time) — say so, or the two read as a contradiction.
              from={<span>{formatDate(proposedDateFrom)}<span className="ml-1 text-xs">(ODD)</span></span>}
              // CR-5479: colour is reserved for early/late only.
              to={(
                <span className={proposedDateVariance?.className ? `inline-block px-1.5 py-0.5 rounded font-semibold ${proposedDateVariance.className}` : 'font-semibold'}>
                  {formatDate(proposedDateTo)}{proposedDateVariance?.suffix || ''}
                </span>
              )}
            />
          )}
          {hasProposedPrice && (
            <ProposedFieldRow
              {...rowProps('price')}
              icon={DollarSign}
              label="Unit Price"
              from={line.unit_price}
              to={<span className="font-semibold text-orange-700">{line.supplier_proposed_unit_price}</span>}
            />
          )}
          {hasProposedQty && (
            <ProposedFieldRow
              {...rowProps('quantity')}
              icon={Package}
              label="Quantity"
              from={formatQuantity(line.quantity)}
              to={<span className="font-semibold text-orange-700">{formatQuantity(line.supplier_proposed_qty)}</span>}
            />
          )}
          {/* CR-7277: say what the state actually is, and offer no action for it. */}
          {!hasProposedDate && !hasProposedPrice && !hasProposedQty && (
            <div className="bg-white rounded-lg p-3 border text-sm text-muted-foreground">
              {phantomChange
                ? 'This line is marked as having a change pending, but no proposed date, quantity or price was recorded against it. There is nothing to accept or reject — raise a new change request if one is needed.'
                : noOpDateChange
                  ? 'The proposed date is the same as the date already agreed, so there is nothing to change. There is nothing to accept or reject.'
                  : 'A change has been proposed. Review the details in the Detail tab.'}
            </div>
          )}
        </div>

        {/* CR-4862: "Reject All" spans every field, so its form stays at the bottom. */}
        {rejectField === 'all' && renderRejectForm('all')}

        {isReviewer && actionable && proposalCount > 1 && !quantityAcceptBlocked && (
          <div className="flex items-center gap-3 pt-2 border-t border-orange-200">
            <button onClick={handleAcceptAll} disabled={!!fieldActionLoading} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50">
              {fieldActionLoading === 'all' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />} Accept All
            </button>
            <button onClick={() => setRejectField('all')} disabled={!!fieldActionLoading} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50">
              <XCircle className="h-4 w-4" /> Reject All
            </button>
          </div>
        )}

        {/* RETIRED 17 Sep 2026 (Aaron, walkthrough batch 3). A second Accept /
            Reject pair used to sit here beneath the per-field rows: *"we have
            duplicate buttons here and way too many buttons."*

            It was not just duplication. That pair opened the date-only
            Accept / Decline modals, and the decline one has no counter field —
            so a SINGLE-field proposal, which is what most of them are, could
            only be refused flat, while a three-field one offered a counter on
            every row: *"there is an option to do a counter proposal... if it's
            a bulk proposal, it seems, but not if it's just one on its own."*
            One route now, the per-field rows above, and the counter comes with
            it whatever the proposal's shape. */}
      </div>
    </div>
  );
}
